import React, { useRef, useState, useEffect } from 'react';
import { Paper, Button, IconButton, Box, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import SaveIcon from '@mui/icons-material/Save';
import { useSelector } from 'react-redux';

/**
 * GlassOverImage - Canvas-based drawing implementation
 * Allows users to draw on top of an image using touch or mouse
 * Stores drawing as JSON array of line objects
 */
const GlassOverImage = ({ id, properties, value, valueUpdaterCallback, validityUpdaterCallback }) => {
    const { options = {}, constrains = {} } = properties;
    const language = useSelector(state => state.App_Settings.language);
    const label = options.label || 'Draw Over Image';
    
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentLine, setCurrentLine] = useState([]);
    const [lines, setLines] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);

    // Sync with parent value
    useEffect(() => {
        if (value && value.lines) {
            setLines(value.lines);
            if (value.imageData) {
                setSelectedImage(value.imageData);
            }
        }
    }, [value]);

    // Update parent whenever lines or image changes
    useEffect(() => {
        if (imageLoaded || lines.length > 0) {
            const drawingData = {
                lines: lines,
                imageData: selectedImage,
                timestamp: new Date().toISOString(),
            };
            valueUpdaterCallback(drawingData);
            validityUpdaterCallback(lines.length > 0 && selectedImage !== null);
        }
    }, [lines, selectedImage, imageLoaded]);

    // Redraw canvas when lines change
    useEffect(() => {
        if (imageLoaded && canvasRef.current) {
            redrawCanvas();
        }
    }, [lines, imageLoaded]);

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setSelectedImage(e.target.result);
                setImageLoaded(false); // Reset to false, will be set true after canvas is ready
            };
            reader.readAsDataURL(file);
        }
    };

    // Initialize canvas when image is selected
    useEffect(() => {
        if (selectedImage && canvasRef.current && !imageLoaded) {
            const img = new Image();
            img.onload = () => {
                const canvas = canvasRef.current;
                if (canvas) {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    setImageLoaded(true);
                }
            };
            img.src = selectedImage;
        }
    }, [selectedImage, imageLoaded]);

    const getCoordinates = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        let clientX, clientY;

        if (event.touches && event.touches.length > 0) {
            // Touch event
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else {
            // Mouse event
            clientX = event.clientX;
            clientY = event.clientY;
        }

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY,
        };
    };

    const startDrawing = (event) => {
        event.preventDefault();
        setIsDrawing(true);
        const coords = getCoordinates(event);
        setCurrentLine([coords]);
    };

    const draw = (event) => {
        if (!isDrawing) return;
        event.preventDefault();
        
        const coords = getCoordinates(event);
        const newLine = [...currentLine, coords];
        setCurrentLine(newLine);

        // Draw on canvas in real-time
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        if (currentLine.length > 0) {
            ctx.strokeStyle = options.strokeColor || '#FF0000';
            ctx.lineWidth = options.strokeWidth || 3;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            
            ctx.beginPath();
            const lastPoint = currentLine[currentLine.length - 1];
            ctx.moveTo(lastPoint.x, lastPoint.y);
            ctx.lineTo(coords.x, coords.y);
            ctx.stroke();
        }
    };

    const stopDrawing = () => {
        if (isDrawing && currentLine.length > 0) {
            const newLineObject = {
                points: currentLine,
                color: options.strokeColor || '#FF0000',
                width: options.strokeWidth || 3,
                timestamp: Date.now(),
            };
            setLines([...lines, newLineObject]);
            setCurrentLine([]);
        }
        setIsDrawing(false);
    };

    const redrawCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Redraw image
        if (selectedImage) {
            const img = new Image();
            img.onload = () => {
                ctx.drawImage(img, 0, 0);
                
                // Redraw all lines
                lines.forEach((line) => {
                    if (line.points && line.points.length > 1) {
                        ctx.strokeStyle = line.color;
                        ctx.lineWidth = line.width;
                        ctx.lineCap = 'round';
                        ctx.lineJoin = 'round';
                        
                        ctx.beginPath();
                        ctx.moveTo(line.points[0].x, line.points[0].y);
                        for (let i = 1; i < line.points.length; i++) {
                            ctx.lineTo(line.points[i].x, line.points[i].y);
                        }
                        ctx.stroke();
                    }
                });
            };
            img.src = selectedImage;
        }
    };

    const handleUndo = () => {
        if (lines.length > 0) {
            const newLines = lines.slice(0, -1);
            setLines(newLines);
        }
    };

    const handleClear = () => {
        setLines([]);
        if (canvasRef.current && selectedImage) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            
            // Redraw only the image
            const img = new Image();
            img.onload = () => {
                ctx.drawImage(img, 0, 0);
            };
            img.src = selectedImage;
        }
    };

    const exportDrawing = () => {
        const drawingData = {
            lines: lines,
            imageData: selectedImage,
            metadata: {
                timestamp: new Date().toISOString(),
                totalLines: lines.length,
                totalPoints: lines.reduce((sum, line) => sum + line.points.length, 0),
            }
        };
        
        // Log to console
        console.log('Drawing Data (JSON):', JSON.stringify(drawingData, null, 2));
        
        // Create downloadable JSON file
        const jsonString = JSON.stringify(drawingData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `drawing-${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        return drawingData;
    };

    return (
        <Paper elevation={3} sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>
                {label} (Canvas Implementation)
            </Typography>

            {!selectedImage ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                    <Button variant="contained" component="label">
                        Upload Image
                        <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleImageUpload}
                        />
                    </Button>
                </Box>
            ) : (
                <Box>
                    <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<UndoIcon />}
                            onClick={handleUndo}
                            disabled={lines.length === 0}
                        >
                            Undo
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={handleClear}
                            disabled={lines.length === 0}
                        >
                            Clear
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<SaveIcon />}
                            onClick={exportDrawing}
                        >
                            Export JSON
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            position: 'relative',
                            border: '2px solid #ccc',
                            borderRadius: 1,
                            overflow: 'hidden',
                            maxWidth: '100%',
                            touchAction: 'none', // Prevent scrolling while drawing
                        }}
                    >
                        <canvas
                            ref={canvasRef}
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                            style={{
                                display: 'block',
                                maxWidth: '100%',
                                height: 'auto',
                                cursor: 'crosshair',
                            }}
                        />
                    </Box>

                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                        Lines drawn: {lines.length}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default GlassOverImage;
